import React, { useState, useEffect } from 'react';

function PresentationMode({ studyGuide, onExit }) {
  const [currentSection, setCurrentSection] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [fontSize, setFontSize] = useState('large');
  const [showControls, setShowControls] = useState(true);

  const sections = studyGuide.sections || [];
  const currentItem = sections[currentSection];

  // Auto-hide controls after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowControls(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, [currentSection]);

  // Show controls on mouse movement
  useEffect(() => {
    const handleMouseMove = () => setShowControls(true);
    document.addEventListener('mousemove', handleMouseMove);

    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const nextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const prevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const goToSection = (index) => {
    setCurrentSection(index);
  };

  const toggleNotes = () => {
    setShowNotes(!showNotes);
  };

  if (!currentItem) {
    return (
      <div className="presentation-mode">
        <div className="presentation-content">
          <h2>No content to display</h2>
          <button className="exit-btn" onClick={onExit}>
            Exit Presentation Mode
          </button>
        </div>
      </div>
    );
  }

  const renderSection = (section) => {
    switch (section.type) {
      case 'verse':
        return (
          <div className="presentation-verse">
            <div className="verse-reference">
              {section.book} {section.chapter}:{section.verse}
            </div>
            <div className="verse-text">{section.text}</div>
            {showNotes && section.notes && (
              <div className="verse-notes">
                <strong>Notes:</strong> {section.notes}
              </div>
            )}
          </div>
        );

      case 'notes':
        return (
          <div className="presentation-notes">
            <h3>{section.title}</h3>
            <div className="notes-content">{section.content}</div>
          </div>
        );

      case 'questions':
        return (
          <div className="presentation-questions">
            <h3>{section.title}</h3>
            <ul>
              {(section.questions || []).map((question, index) => (
                <li key={index}>{question}</li>
              ))}
            </ul>
          </div>
        );

      case 'observations':
        return (
          <div className="presentation-observations">
            <h3>{section.title}</h3>
            <div className="observations-content">{section.content}</div>
          </div>
        );

      default:
        return <div>Unknown section type</div>;
    }
  };

  return (
    <div className="presentation-mode">
      <div className="presentation-content">
        <div className="section-counter">
          {currentSection + 1} of {sections.length}
        </div>

        <div className="presentation-main">
          {renderSection(currentItem)}
        </div>

        {showControls && (
          <div className="presentation-controls">
            <button
              className="nav-btn prev-btn"
              onClick={prevSection}
              disabled={currentSection === 0}
            >
              ← Previous
            </button>

            <div className="controls-center">
              <button className="control-btn" onClick={toggleNotes}>
                {showNotes ? '👁️‍🗨️ Hide Notes' : '👁️ Show Notes'}
              </button>
              <select
                value={fontSize}
                onChange={(e) => setFontSize(e.target.value)}
                className="font-size-select"
              >
                <option value="medium">Medium</option>
                <option value="large">Large</option>
                <option value="xlarge">Extra Large</option>
              </select>
            </div>

            <button
              className="nav-btn next-btn"
              onClick={nextSection}
              disabled={currentSection === sections.length - 1}
            >
              Next →
            </button>
          </div>
        )}

        <div className="section-thumbnails">
          {sections.map((section, index) => (
            <button
              key={section.id}
              className={`thumbnail ${index === currentSection ? 'active' : ''}`}
              onClick={() => goToSection(index)}
              title={`${section.type}: ${section.title || `${section.book} ${section.chapter}:${section.verse}`}`}
            >
              {index + 1}
            </button>
          ))}
        </div>

        <button className="exit-btn" onClick={onExit}>
          Exit Presentation
        </button>
      </div>
    </div>
  );
}

export default PresentationMode;